import React, { useEffect, useRef, useState } from 'react';
import { Camera, Check, Loader2, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import { supabase } from '../lib/supabase';
import { avatarUrlOf, displayNameOf } from '../lib/appModules';
import { Avatar } from '../components/UserMenu';

const AVATAR_BUCKET = 'avatars';
const AVATAR_MAX_BYTES = 3 * 1024 * 1024;
const ROLE_LABEL: Record<string, string> = { admin: 'Administrador', fin_ops: 'FinOps', employee: 'Funcionário' };

/** Name and photo of the signed-in user (auth metadata), plus the organization they belong to. */
export default function AccountSettings() {
  const { user } = useAuth();
  const { org, role } = useOrg();
  const meta = user?.user_metadata as Record<string, unknown> | undefined;
  const [name, setName] = useState('');
  const [photo, setPhoto] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setName(displayNameOf(meta, user?.email));
    setPhoto(avatarUrlOf(meta));
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!saved) return;
    const t = window.setTimeout(() => setSaved(false), 2500);
    return () => window.clearTimeout(t);
  }, [saved]);

  const saveName = async () => {
    const value = name.trim();
    if (!value || !user) return;
    setSaving(true);
    setError(null);
    const { error: err } = await supabase.auth.updateUser({ data: { full_name: value } });
    setSaving(false);
    if (err) setError(err.message);
    else setSaved(true);
  };

  const uploadPhoto = async (file: File) => {
    if (!user) return;
    if (!file.type.startsWith('image/')) { setError('Escolha uma imagem.'); return; }
    if (file.size > AVATAR_MAX_BYTES) { setError('A foto deve ter no máximo 3 MB.'); return; }
    setUploading(true);
    setError(null);
    try {
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
      const path = `${user.id}/avatar-${Date.now()}.${ext}`;
      const { error: upErr } = await supabase.storage.from(AVATAR_BUCKET).upload(path, file, { upsert: true, contentType: file.type });
      if (upErr) throw upErr;
      const { data: { publicUrl } } = supabase.storage.from(AVATAR_BUCKET).getPublicUrl(path);
      const { error: metaErr } = await supabase.auth.updateUser({ data: { avatar_url: publicUrl } });
      if (metaErr) throw metaErr;
      setPhoto(publicUrl);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao enviar a foto.');
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async () => {
    if (!user) return;
    setUploading(true);
    setError(null);
    const { error: err } = await supabase.auth.updateUser({ data: { avatar_url: null } });
    setUploading(false);
    if (err) setError(err.message);
    else { setPhoto(''); setSaved(true); }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Configurações da conta</h1>
          <p className="subtitle">Seu nome e sua foto aparecem no menu e para quem compartilha projetos com você.</p>
        </div>
      </div>

      {error && <div className="alert-banner alert-warning"><span>{error}</span></div>}

      <div className="glass-card account-card">
        <h2>Perfil</h2>
        <div className="account-photo-row">
          <Avatar photo={photo} name={name || displayNameOf(meta, user?.email)} size={84} />
          <div className="account-photo-actions">
            <button type="button" className="btn-secondary" disabled={uploading} onClick={() => fileRef.current?.click()}>
              {uploading ? <Loader2 size={16} className="account-spin" /> : <Camera size={16} />} Trocar foto
            </button>
            {photo && (
              <button type="button" className="btn-secondary" disabled={uploading} onClick={() => { void removePhoto(); }}>
                <Trash2 size={16} /> Remover
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f) void uploadPhoto(f);
              }}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="account-name">Nome</label>
          <input id="account-name" type="text" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />
        </div>
        <div className="form-group">
          <label htmlFor="account-email">E-mail</label>
          <input id="account-email" type="email" value={user?.email || ''} readOnly disabled />
        </div>
        <div className="account-actions">
          <button type="button" className="btn-primary" style={{ width: 'auto' }} disabled={saving || !name.trim()} onClick={() => { void saveName(); }}>
            {saving ? <Loader2 size={16} className="account-spin" /> : saved ? <Check size={16} /> : null} {saved ? 'Salvo' : 'Salvar'}
          </button>
        </div>
      </div>

      <div className="glass-card account-card">
        <h2>Organização</h2>
        <dl className="account-dl">
          <div><dt>Empresa</dt><dd>{org?.name || '—'}</dd></div>
          <div><dt>Seu papel</dt><dd>{role ? ROLE_LABEL[role] || role : '—'}</dd></div>
          <div><dt>Plano</dt><dd>{org?.plans?.name || '—'}</dd></div>
          <div><dt>Créditos</dt><dd>{(org?.available_credits ?? 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}</dd></div>
        </dl>
      </div>
    </div>
  );
}
